import { type PercentageInputProps, PercentageInput } from "$root/components/PercentageInput";
import { FormController } from "$root/third-party-integrations/react-hook-form";
import { valueByPath } from "$root/utils";
import type { StylableProps } from "@mdotm/shadcn/legacy/components";
import { type NodeOrFn, renderNodeOrFn } from "@mdotm/shadcn/legacy/react-extensions";
import { FormField, Label } from "@mdotm/shadcn/mdotm/components";
import type { Control, FieldValues, FormState, Path } from "react-hook-form";

export type PercentageFieldProps<T extends FieldValues> = StylableProps & {
	control: Control<T, any>;
	formState: FormState<T>;
	name: Path<T>;
	label: NodeOrFn<{ htmlFor: string }>;
	hideBanner?: boolean;
} & Omit<PercentageInputProps, "value">;

export function PercentageField<T extends FieldValues>({
	label,
	name,
	formState,
	control,
	className,
	style,
	hideBanner = false,
	...forward
}: PercentageFieldProps<T>): React.JSX.Element {
	return (
		<FormField
			className={className}
			style={style}
			label={({ htmlFor }) =>
				typeof label === "string" ? (
					<Label htmlFor={htmlFor} className="mb-px">
						{label}
					</Label>
				) : (
					renderNodeOrFn(label, { htmlFor })
				)
			}
			error={hideBanner ? undefined : (valueByPath(formState.errors, name) as { message?: string })?.message}
		>
			{(fieldProps) => (
				<FormController
					control={control}
					name={name}
					render={({ field: { ref, ...controllerProps } }) => (
						<PercentageInput innerRef={ref} {...fieldProps} {...controllerProps} {...forward} />
					)}
				/>
			)}
		</FormField>
	);
}
